/* eslint-disable max-lines */
import _ from 'underscore'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import Form from 'pages/shared/Form'
import PreviewField from 'pages/shared/TrendForm/PreviewField'
import TrendAlerts from 'pages/shared/TrendForm/TrendAlerts'
import FormSection from 'pages/shared/FormSection'

import Checkbox from 'components/Checkbox'
import TextInput from 'components/TextInput'
import ListSelector from 'components/ListSelectAndSort'
import LabelledField from 'components/LabelledField'
import ErrorMessage from 'components/ErrorMessage'

import useDeviceMetrics from 'hooks/useDeviceMetrics'
import { useStoreContext } from 'hooks/StoreContext'
import useGroups from 'hooks/useGroups'

import { scrubText } from 'helpers/inputCleaners'

import TrendModel, { defaultTrend } from 'models/Trend'

import { color } from 'styles/colors'
import { LoadingSinglePanePage, screenSize } from 'styles/shared'

import { Trend, TrendErrors } from 'types/DeviceMetric'

interface List {
  id: string | number,
  tag: string | null,
  title: string,
  color?: string | undefined,
  type: 'metric' | 'group',
  unit: string
}

type Props = {
  title: string,
  saveMethod: 'create' | 'update',
  trend?: Trend
}

const trendModel = new TrendModel()

const TrendForm = ({ title, saveMethod, trend = defaultTrend }: Props) => {
  const navigate = useNavigate()
  useDeviceMetrics()

  const { deviceMetrics, areDeviceMetricsLoaded, setAreTrendsLoaded } = useStoreContext()
  const { groups, areGroupsLoaded } = useGroups()

  const [formAttributes, setFormAttributes] = useState<Trend>(trend)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedItems, setSelectedItems] = useState<List[]>([])
  const [showPreview, setShowPreview] = useState(false)

  const alertList = formAttributes.alertList

  const { draft, name, metricList, groupList, drawLines, plotZero, waterMark } = formAttributes
  const [errors, setErrors] = useState<Partial<TrendErrors>>({ name: '', selectedList: '' })
  const onchange = (updateAttributes) => {
    setFormAttributes({ ...formAttributes, ...updateAttributes })
  }

  const onAlertListChange = (alertList) => {
    const sortedAlerts = alertList.map((item, index) => ({ id: item.id, title: item.title, code: item.id, sortPosition: index }))
    setFormAttributes((prevAttributes) => ({ ...prevAttributes, alertList: sortedAlerts }))
  }

  const onListChange = (selectedItems) => {
    const metricIds = new Set(deviceMetrics.map(({ id }) => id))
    const mList: { metricId: string; sortPosition: number; unit: string, draft: boolean }[] = []
    const gList: { groupId: number; sortPosition: number; unit: string, draft: boolean }[] = []

    selectedItems.forEach(({ id, unit, tag }, index) => {
      const item = { sortPosition: index + 1 }
      if (metricIds.has(id)) {
        mList.push({ ...item, metricId: id, unit, draft: tag === 'draft' })
      } else {
        gList.push({ ...item, groupId: id, unit, draft: tag === 'draft' })
      }
    })

    setFormAttributes((prevAttributes) => ({ ...prevAttributes, metricList: mList, groupList: gList }))

    setSelectedItems(selectedItems)
    setShowPreview(false)
  }

  useEffect(() => {
    if (areDeviceMetricsLoaded && areGroupsLoaded) {
      const combinedList = (metricList, groupList) => {
        return [
          ...(metricList || []).map(list => {
            const metric = _.find(deviceMetrics, (metric) => metric.id === list.metricId)
            return {
              id: list.metricId,
              title: metric.name,
              sortPosition: list.sortPosition,
              tag: metric.draft ? 'draft' : null,
              color: metric.color,
              unit: metric.unit,
              type: 'metric'
            }
          }
          ),
          ...(groupList || []).map(list => {
            const group = _.find(groups, (group) => group.groupId === list.groupId)

            return {
              id: list.groupId,
              sortPosition: list.sortPosition,
              title: group?.name,
              tag: group?.draft ? 'draft' : null,
              unit: group?.unit,
              type: 'group'
            }
          })
        ]
      }

      const sortedSelectedList = _.chain(combinedList(metricList, groupList))
        .sortBy((list) => list.sortPosition)
        .map((list) => (
          _.omit(list, 'sortPosition')
        )).value()

      setSelectedItems(_.values(sortedSelectedList))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areDeviceMetricsLoaded, areGroupsLoaded, trend, groups])

  const onSave = useCallback(() => {
    setIsSaving(true)

    const onSaveSuccessCallback = () => {
      setAreTrendsLoaded(false)
      navigate('/admin/trends')
    }

    const onValidationErrorCallback = (errors) => {
      setErrors(errors)
      setIsSaving(false)
    }

    const onSaveFailureCallback = () => {
      setIsSaving(false)
      alert('Save Failed')
    }

    trendModel.save(formAttributes, saveMethod, onSaveSuccessCallback, onSaveFailureCallback, onValidationErrorCallback)
  }, [navigate, formAttributes, saveMethod, setAreTrendsLoaded])

  const onCancel = () => {
    const hasChanges = !_.isEqual(formAttributes, { ...trend })

    if (!hasChanges || (hasChanges && confirm('Are you sure you want to cancel?'))) {
      navigate('/admin/trends')
    }
  }

  const onDelete = useCallback(() => {
    if (confirm('Are you sure you want to delete this Trend permanently? This cannot be undone.')) {
      setIsDeleting(true)

      const onDeleteSuccessCallback = () => {
        setAreTrendsLoaded(false)
        navigate('/admin/trends')
      }

      const onDeleteFailureCallback = () => {
        setIsDeleting(false)
        alert('Delete Failed')
      }

      trendModel.delete(onDeleteSuccessCallback, onDeleteFailureCallback, trend.id)
    }
  }, [navigate, trend.id, setAreTrendsLoaded])

  const getAvailableMetrics = () => {
    const selectedIds = selectedItems.length > 0 ? _.pluck(selectedItems, 'id') : undefined

    const allDeviceMetrics = deviceMetrics.map(({ id, name, draft, color, unit }) => {
      return { id, title: name, tag: draft ? 'draft' : null, color, unit }
    })

    return selectedIds ? _.filter(allDeviceMetrics, metric => !_.includes(selectedIds, metric.id)) : allDeviceMetrics
  }

  const getSelectedAlerts = () => {
    const aList: { id: string | undefined; code: string | undefined; title: string | undefined}[] = []
    if (alertList) {
      alertList.forEach(({ code }) => {
        aList.push({
          id: code,
          title: code,
          code
        })
      })
    }

    return aList
  }

  const getAvailableGroups = () => {
    const selectedIds = selectedItems.length > 0 ? _.pluck(selectedItems, 'id') : undefined
    const allGroups = groups.map(({ groupId, name, draft, unit }) => ({ id: groupId, title: name, tag: draft ? 'draft' : null, unit }))

    return selectedIds ? _.filter(allGroups, group => !_.includes(selectedIds, group.id)) : allGroups
  }

  const onPreviewButton = () => {
    const validationErrors = trendModel.validate(formAttributes)
    if (_.isEmpty(validationErrors) && !showPreview) {
      setShowPreview(true)
    } else {
      setShowPreview(false)
    }
    setErrors(validationErrors)
  }

  if (!areDeviceMetricsLoaded || !areGroupsLoaded) {
    return <LoadingSinglePanePage />
  }

  return (
    <Form
      fullScreen
      title={title}
      isDeleting={isDeleting}
      isSaving={isSaving}
      deleteButtonText={'Trend'}
      displayDeleteButton={saveMethod === 'update'}
      onSaveCallback={onSave}
      onCancelCallback={onCancel}
      onDeleteCallback={onDelete}
    >
      <S.Subtitle>Trend</S.Subtitle>
      <Checkbox
        label="Draft"
        checked={draft}
        onChange={() => onchange({ draft: !draft })}
      />
      <S.TextInput
        label="Name"
        isRequired
        onChange={event => onchange({ name: scrubText(event.target.value, true) })}
        value={name || ''}
        errorMessage={errors?.name}
      />
      <FormSection title="Chart Options">
        <Checkbox
          label="Plot 0"
          checked={plotZero}
          onChange={() => onchange({ plotZero: !plotZero })}
        />
        <Checkbox
          label="Draw Lines"
          checked={drawLines}
          onChange={() => onchange({ drawLines: !drawLines })}
        />
        <S.TextInput
          label="Watermark"
          onChange={event => onchange({ waterMark: scrubText(event.target.value, true) })}
          value={waterMark || ''}
        />
      </FormSection>
      <TrendAlerts
        TrendAlerts={getSelectedAlerts()}
        onAlertListChangeCallback={onAlertListChange}
      />
      <FormSection title="Select Metrics/Groups">
        <ListSelector
          subTitle="Add Metrics/Groups"
          selectedListLabel="Selected Metrics/Groups"
          availableListLabel="Available Metrics/Groups"
          initialSelected={selectedItems}
          initialAvailable={[...getAvailableMetrics(), ...getAvailableGroups()]}
          onChangeCallback={onListChange}
        />
        <S.ErrorMessage errorMessage={errors.selectedList} />
      </FormSection>
      {selectedItems.length > 0 &&
        <Button
          decoration="positive"
          onPress={onPreviewButton}
          text={!showPreview ? 'Preview Trend' : 'Hide Preview'}
          styles={{ button: { marginBottom: 15 } }}
          aria-label={!showPreview ? 'preview-trend' : 'hide-preview'}
        />
      }
      {showPreview &&
        <S.PreviewField
          trend={formAttributes}
          metricList={deviceMetrics}
          groupList={groups}
          list={selectedItems}
          alerts={alertList || []}
        />
      }
    </Form>
  )
}

export default TrendForm

const S = {
  Form: styled(Form)`
    max-width: 100%;
  `,
  Subtitle: styled.h2`
    margin-top: 20px;
    border-bottom: solid 1px;
    border-color: ${color('border')};
    padding-bottom: 10px;
    text-transform: uppercase;
  `,
  SelectedListField: styled(LabelledField)`
    padding-top: 20px;
  `,
  TextInput: styled(TextInput)`
    width: 50%;

    @media ${screenSize.small} {
      width: 100%;
    }
  `,
  PreviewField: styled(PreviewField)`
    margin-top: 50px;
    padding: 20px;
  `,
  ErrorMessage: styled(ErrorMessage)`
    padding-top: 5px;
    margin-bottom: -25px;
  `
}
